import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/rootReducer";
import type {
  CreateMembershipSkuFormData,
  MembershipSku,
  UpdateMembershipSkuFormData,
} from "../schemas/membershipSkuSchema";
import { fetchMembershipSkus as fetchMembershipSkusApi } from "@/api/fetchMembershipSkus/fetchMembershipSkusApi";
import { fetchMembershipSkuByUuid as fetchMembershipSkuByUuidApi } from "@/api/fetchMembershipSkuByUuid/fetchMembershipSkuByUuidApi";
import { createMembershipSku as createMembershipSkuApi } from "@/api/createMembershipSku/createMembershipSkuApi";
import { updateMembershipSku as updateMembershipSkuApi } from "@/api/updateMembershipSku/updateMembershipSkuApi";
import { deleteMembershipSku as deleteMembershipSkuApi } from "@/api/deleteMembershipSku/deleteMembershipSkuApi";

interface MembershipSkuState {
  skus: MembershipSku[];
  loading: boolean;
  error: string | null;
  currentSku: MembershipSku | null;
}

const initialState: MembershipSkuState = {
  skus: [],
  loading: false,
  error: null,
  currentSku: null,
};

export const fetchMembershipSkus = createAsyncThunk(
  "membershipSku/fetchAll",
  async (tenant?: string) => {
    return await fetchMembershipSkusApi(tenant);
  },
);

export const fetchMembershipSkuByUuid = createAsyncThunk(
  "membershipSku/fetchByUuid",
  async (uuid: string) => {
    return await fetchMembershipSkuByUuidApi(uuid);
  },
);

export const createMembershipSku = createAsyncThunk(
  "membershipSku/create",
  async (data: CreateMembershipSkuFormData) => {
    return await createMembershipSkuApi(data);
  },
);

export const updateMembershipSku = createAsyncThunk(
  "membershipSku/update",
  async ({
    uuid,
    data,
  }: {
    uuid: string;
    data: UpdateMembershipSkuFormData;
  }) => {
    return await updateMembershipSkuApi(uuid, data);
  },
);

export const deleteMembershipSku = createAsyncThunk(
  "membershipSku/delete",
  async (uuid: string) => {
    await deleteMembershipSkuApi(uuid);
    return uuid;
  },
);

const membershipSkuSlice = createSlice({
  name: "membershipSku",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSku: (state, action: PayloadAction<MembershipSku | null>) => {
      state.currentSku = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch all SKUs
    builder.addCase(fetchMembershipSkus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMembershipSkus.fulfilled, (state, action) => {
      state.loading = false;
      state.skus = action.payload;
    });
    builder.addCase(fetchMembershipSkus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch membership SKUs";
    });

    // Fetch single SKU
    builder.addCase(fetchMembershipSkuByUuid.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMembershipSkuByUuid.fulfilled, (state, action) => {
      state.loading = false;
      state.currentSku = action.payload;
    });
    builder.addCase(fetchMembershipSkuByUuid.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch membership SKU";
    });

    // Create SKU
    builder.addCase(createMembershipSku.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createMembershipSku.fulfilled, (state, action) => {
      state.loading = false;
      state.skus.push(action.payload);
    });
    builder.addCase(createMembershipSku.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to create membership SKU";
    });

    // Update SKU
    builder.addCase(updateMembershipSku.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateMembershipSku.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.skus.findIndex(
        (sku) => sku.uuid === action.payload.uuid,
      );
      if (index !== -1) {
        state.skus[index] = action.payload;
      }
      if (state.currentSku?.uuid === action.payload.uuid) {
        state.currentSku = action.payload;
      }
    });
    builder.addCase(updateMembershipSku.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to update membership SKU";
    });

    // Delete SKU
    builder.addCase(deleteMembershipSku.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteMembershipSku.fulfilled, (state, action) => {
      state.loading = false;
      state.skus = state.skus.filter((sku) => sku.uuid !== action.payload);
      if (state.currentSku?.uuid === action.payload) {
        state.currentSku = null;
      }
    });
    builder.addCase(deleteMembershipSku.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to delete membership SKU";
    });
  },
});

export const { setCurrentSku } = membershipSkuSlice.actions;

/**
 * Selectors
 */
export const selectMembershipSkus = (state: RootState) =>
  state.membershipSku.skus;
export const selectMembershipSkuLoading = (state: RootState) =>
  state.membershipSku.loading;
export const selectCurrentMembershipSku = (state: RootState) =>
  state.membershipSku.currentSku;

export default membershipSkuSlice.reducer;
